import { Component, OnDestroy, computed, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InstalledAppleApplication } from '@rocksoft/locations-sdk';
import { AppleApplicationLockChanged, AppleDeviceDesiredNameChanged, Device } from '@rocksoft/shared';
import { Subscription } from 'rxjs';
import { EditDeviceNameModalComponent } from '../modals/edit-device-name-modal/edit-device-name-modal.component';

@Component({
  selector: 'era-apple-device-ui',
  templateUrl: './apple-device.component.html',
  styleUrls: ['./apple-device.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FaIconComponent],
})
export class AppleDeviceComponent implements OnDestroy {
  private readonly modalService = inject(NgbModal);

  public readonly device = input<Device | undefined>(undefined);
  public readonly isAtLatestAllowedOsVersion = input(true);

  readonly displayDeviceNameOverride = computed(() => {
    const device = this.device();
    if (!device) return false;

    return (
      device.name != undefined &&
      device.deviceNameOverride != undefined &&
      !device.name.endsWith(` ${device.deviceNameOverride}`)
    );
  });

  public readonly applicationLockChange = output<AppleApplicationLockChanged>();

  public readonly deviceNameOverrideChange = output<AppleDeviceDesiredNameChanged>();

  public readonly updateDeviceEvent = output<string>();

  private modalSubscriptions?: Subscription;

  public callUpdate(udid: string) {
    this.updateDeviceEvent.emit(udid);
  }

  public callLock(udid: string, application: InstalledAppleApplication) {
    this.applicationLockChange.emit(new AppleApplicationLockChanged(udid, application.identifier, true));
  }

  public callUnlock(udid: string, application: InstalledAppleApplication) {
    this.applicationLockChange.emit(new AppleApplicationLockChanged(udid, application.identifier, false));
  }

  public editName() {
    const device = this.device();
    if (!device) {
      return;
    }
    const modalRef = this.modalService.open(EditDeviceNameModalComponent);
    const instance = modalRef.componentInstance as EditDeviceNameModalComponent;
    instance.initialName = device.deviceNameOverride;

    this.modalSubscriptions = modalRef.closed.subscribe((x) => {
      this.deviceNameOverrideChange.emit(new AppleDeviceDesiredNameChanged(device.udid, x.newName));
      this.unsubscribe();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe();
  }

  private unsubscribe() {
    if (this.modalSubscriptions !== undefined) {
      this.modalSubscriptions.unsubscribe();
    }
  }
}
